import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { mergeWith } from 'lodash-es';

import { isStoreEntityKey } from './schemas';
import type { EntitiesState, PartialEntities, StoreEntityKey } from './types';

const initialState: EntitiesState = {
  acl_templates: {},
  application_tokens: {},
  applications: {},
  approvals: {},
  approval_requests: {},
  asset_persons: {},
  asset_relations: {},
  assets: {},
  automations: {},
  collection_keyframes: {},
  collections: {},
  custom_actions: {},
  discoveries: {},
  discoveries_admin: {},
  export_locations: {},
  file_sets: {},
  files: {},
  formats: {},
  groups: {},
  groups_basic: {},
  identity_providers: {},
  jobs: {},
  keyframes: {},
  logos: {},
  metadata_fields: {},
  metadata_unmerged_views: {},
  metadata_user_fields: {},
  metadata_views: {},
  search_views: {},
  notifications: {},
  person_instances: {},
  persons: {},
  playlists: {},
  portals: {},
  proxies: {},
  projects: {},
  recycle_bin_file_sets: {},
  recycle_bin_formats: {},
  relation_types: {},
  role_groups: {},
  role_groups_basic: {},
  saved_search_results: {},
  saved_searches: {},
  search_backends: {},
  search_history: {},
  search_items: {},
  segments: {},
  share_users: {},
  shares: {},
  storage_gateways: {},
  storage_gateway_clusters: {},
  storages: {},
  subtitles: {},
  system_domains: {},
  system_domains_basic: {},
  system_logs_recipients: {},
  teams: {},
  teams_basic: {},
  themes: {},
  transcoders: {},
  transcription_properties: {},
  users: {},
  users_basic: {},
  recent_search_items: {},
  webhooks: {},
};

export const entitiesSlice = createSlice({
  name: 'entities',
  initialState,
  reducers: {
    updateEntities: (
      state: EntitiesState,
      action: PayloadAction<PartialEntities>,
    ) => {
      // To avoid merging the arrays - treat them as simple values
      // Update all entity keys separately to avoid recreation of the whole entities
      // state every time
      for (const [key, entities] of Object.entries(action.payload)) {
        if (!isStoreEntityKey(key)) {
          continue;
        }
        mergeWith(state[key], entities, (objValue, srcValue) =>
          Array.isArray(objValue) ? srcValue : undefined,
        );
      }
    },
    replaceEntities: (state, action: PayloadAction<Partial<EntitiesState>>) => {
      let updatedEntities = {};
      for (const [key, entities] of Object.entries(action.payload)) {
        if (!isStoreEntityKey(key)) {
          continue;
        }
        updatedEntities = {
          ...updatedEntities,
          [key]: { ...state[key], ...entities },
        };
      }
      return {
        ...state,
        ...updatedEntities,
      };
    },
    clearNormalizedEntities: (state, action: PayloadAction<StoreEntityKey>) => {
      state[action.payload] = {};
    },
    deleteNormalizedEntity: (
      state,
      action: PayloadAction<{ key: StoreEntityKey; id: string }>,
    ) => {
      const { key, id } = action.payload;
      delete state[key][id];
    },
  },
});

export const {
  updateEntities,
  replaceEntities,
  clearNormalizedEntities,
  deleteNormalizedEntity,
} = entitiesSlice.actions;

export const entitiesReducer = entitiesSlice.reducer;
